import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api/client';
import { queryKeys } from '../../../shared/api/query-keys';

export type TaxType = 'ICMS' | 'ISS' | 'PIS' | 'COFINS' | 'IPI' | 'IBS' | 'CBS';

/** Espelha `TaxRateResponseDto`, campo a campo. */
export interface TaxRate {
  id: string;
  tenantId: string;
  taxType: string;
  aliquota: number;
  ufOrigem: string | null;
  ufDestino: string | null;
  municipioIbge: string | null;
  regimeDestinatario: string | null;
  ncmPrefix: string | null;
  reducao: number | null;
  mva: number | null;
  effectiveSince: string;
  effectiveUntil: string | null;
  rulePriority: number;
  source: string;
  createdAt: string;
  updatedAt: string;
}

export interface TaxRateInput {
  taxType: TaxType;
  aliquota: number;
  ncmPrefix?: string | null;
  ufOrigem?: string | null;
  ufDestino?: string | null;
  reducao?: number | null;
  mva?: number | null;
  effectiveSince: string;
  effectiveUntil?: string | null;
}

/**
 * Aliquota em vigor na data de hoje.
 *
 * Nao existe campo "ativo": o que existe e vigencia. Uma aliquota com
 * `effectiveUntil` no passado nao foi desligada — ela venceu.
 */
export function isInForce(rate: Pick<TaxRate, 'effectiveSince' | 'effectiveUntil'>): boolean {
  const today = new Date();
  const since = new Date(rate.effectiveSince);
  if (!Number.isNaN(since.getTime()) && since > today) return false;
  if (!rate.effectiveUntil) return true;
  const until = new Date(rate.effectiveUntil);
  return Number.isNaN(until.getTime()) || until >= today;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface TaxRateQuery {
  taxType?: string;
  /* `QueryTaxRateDto` aceita os tres desde sempre; a tela mandava so o
     tributo e filtrava o resto em lugar nenhum. */
  ufOrigem?: string;
  ufDestino?: string;
  ncmPrefix?: string;
  page?: number;
  limit?: number;
}

export function useTaxRates(params?: TaxRateQuery) {
  return useQuery({
    queryKey: [...queryKeys.fiscal.taxRates(), params],
    queryFn: () =>
      apiClient.get<PaginatedResponse<TaxRate>>('/fiscal/tax-rates', {
        params: {
          page: String(params?.page ?? 1),
          limit: String(params?.limit ?? 50),
          /* O backend le `taxType`; mandar `imposto` nao filtrava nada. */
          ...(params?.taxType && params.taxType !== 'ALL' ? { taxType: params.taxType } : {}),
          ...(params?.ufOrigem ? { ufOrigem: params.ufOrigem } : {}),
          ...(params?.ufDestino ? { ufDestino: params.ufDestino } : {}),
          ...(params?.ncmPrefix ? { ncmPrefix: params.ncmPrefix } : {}),
        },
      }),
  });
}

export function useCreateTaxRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TaxRateInput) =>
      apiClient.post<TaxRate>('/fiscal/tax-rates', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.fiscal.taxRates() }),
  });
}

export function useUpdateTaxRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: TaxRateInput & { id: string }) =>
      apiClient.put<TaxRate>(`/fiscal/tax-rates/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.fiscal.taxRates() }),
  });
}

export function useDeleteTaxRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/fiscal/tax-rates/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.fiscal.taxRates() }),
  });
}
